import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CheckCircleFilled,
  CloseCircleOutlined,
  ExclamationCircleFilled,
  IdcardOutlined,
  SearchOutlined,
  StopFilled
} from "@ant-design/icons";
import { Alert, Button, DatePicker, Input, Popconfirm, Progress, Segmented, Select, Tag, message } from "antd";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import ReportButton from "@controls/report-button";
import { REPORT_VID } from "@components/service/reports/report-types";
import { getWorkplaceNumber } from "@core/runtime-settings";
import {
  cancelNutritionRegistration,
  fetchNutritionLookups,
  fetchNutritionRegistration,
  lookupNutritionCard,
  registerNutritionGuest
} from "./nutrition-api";
import {
  emptyLookups,
  field,
  isoDate,
  lawAllowed,
  lookupOptions,
  normalizeLookups,
  numberField,
  showError
} from "./nutrition-utils";
import "./nutrition.css";

const STATUS_RESET_MS = 8000;

const NutritionRegistration = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const law = useSelector((state) => state.user.session?.law);
  const allowed = lawAllowed(law, 83);
  const scannerRef = useRef(null);
  const [lookups, setLookups] = useState(emptyLookups);
  const [date, setDate] = useState(dayjs());
  const [part, setPart] = useState(1);
  const [idBranch, setIdBranch] = useState(() => getWorkplaceNumber("idBranch", 0));
  const [page, setPage] = useState({ total: 0, registered: 0, waiting: 0, items: [] });
  const [loading, setLoading] = useState(false);
  const [scanner, setScanner] = useState("");
  const [manual, setManual] = useState("");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [card, setCard] = useState(null);
  const [status, setStatus] = useState({ kind: "idle" });
  const [busyKey, setBusyKey] = useState(null);

  const load = useCallback(async () => {
    if (!allowed) return;
    setLoading(true);
    try {
      const data = await fetchNutritionRegistration(isoDate(date), part, idBranch);
      setPage({
        total: numberField(data, "total"),
        registered: numberField(data, "registered"),
        waiting: numberField(data, "waiting"),
        idBranch: numberField(data, "idBranch"),
        items: field(data, "items", []) || []
      });
      if (!idBranch && numberField(data, "idBranch")) setIdBranch(numberField(data, "idBranch"));
    } catch (error) {
      showError(messageApi, error, "Не удалось загрузить регистрацию");
    } finally {
      setLoading(false);
    }
  }, [allowed, date, part, idBranch, messageApi]);

  useEffect(() => {
    fetchNutritionLookups()
      .then((data) => {
        const normalized = normalizeLookups(data);
        setLookups(normalized);
        setPart(normalized.parts[0]?.id || 1);
        if (!normalized.canChooseBranch) {
          setIdBranch((current) => current || getWorkplaceNumber("idBranch", 0));
        }
      })
      .catch((e) => showError(messageApi, e, "Не удалось загрузить справочники"));
  }, [messageApi]);
  useEffect(() => void load(), [load]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      const el = document.activeElement;
      if (!el || el === document.body) scannerRef.current?.focus();
    }, 3000);
    scannerRef.current?.focus();
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!status || status.kind === "idle") return undefined;
    const timer = window.setTimeout(() => setStatus({ kind: "idle" }), STATUS_RESET_MS);
    return () => window.clearTimeout(timer);
  }, [status]);

  const requestFor = (value) => ({
    fdate: isoDate(date),
    part,
    idBranch,
    idNumber: numberField(value, "idNumber"),
    idDoc: numberField(value, "idDoc"),
    idStr: numberField(value, "idStr")
  });

  const applyRegistration = async (value, register) => {
    setLoading(true);
    setBusyKey(`${numberField(value, "idDoc")}:${numberField(value, "idStr")}`);
    try {
      const result = register
        ? await registerNutritionGuest(requestFor(value))
        : await cancelNutritionRegistration(requestFor(value));
      setCard(result);
      setStatus(register
        ? { kind: "ok", card: result, text: "Питание зарегистрировано" }
        : { kind: "idle" });
      await load();
    } catch (error) {
      setStatus({ kind: "denied", text: error.message || "Операция отклонена" });
    } finally {
      setLoading(false);
      setBusyKey(null);
      scannerRef.current?.focus();
    }
  };

  const lookup = async (key, autoRegister = false) => {
    const clean = key.trim();
    if (!clean) return;
    setLoading(true);
    try {
      const found = await lookupNutritionCard(clean, isoDate(date), part, idBranch);
      setCard(found);
      const registeredAt = field(found, "registeredAt");
      const eligible = Boolean(numberField(found, "eligible", field(found, "eligible", false) ? 1 : 0));
      if (registeredAt) {
        setStatus({ kind: "already", card: found, registeredAt });
      } else if (!eligible) {
        setStatus({ kind: "denied", card: found, text: "Регистрация запрещена: питание гостю не положено" });
      } else {
        setStatus({ kind: "ok", card: found, text: "Гость найден" });
        if (autoRegister) await applyRegistration(found, true);
      }
    } catch (error) {
      setCard(null);
      const notFound = /не найден|not found/i.test(error?.message || "");
      setStatus({ kind: notFound ? "notfound" : "denied", text: notFound ? "Карта не найдена" : (error.message || "Ошибка поиска карты") });
    } finally {
      setLoading(false);
      setScanner("");
      window.setTimeout(() => scannerRef.current?.focus(), 0);
    }
  };

  const cardItem = useMemo(() => {
    if (!card) return null;
    return page.items.find((r) =>
      numberField(r, "idDoc") === numberField(card, "idDoc") &&
      (!numberField(card, "idStr") || numberField(r, "idStr") === numberField(card, "idStr"))
    ) || null;
  }, [card, page.items]);

  const visibleItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    return page.items.filter((r) => {
      if (filter === "registered" && !field(r, "registeredAt")) return false;
      if (filter === "waiting" && field(r, "registeredAt")) return false;
      if (!query) return true;
      const haystack = [field(r, "guestName", ""), field(r, "numberName", "")].join(" ").toLowerCase();
      return haystack.includes(query) || haystack.replace(/№\s*/g, "").includes(query.replace(/№\s*/g, ""));
    });
  }, [page.items, filter, search]);

  useEffect(() => {
    const timer = window.setInterval(load, 60000);
    return () => window.clearInterval(timer);
  }, [load]);

  const statusInfo = status?.kind || "idle";
  const statusCard = {
    idle: { icon: <IdcardOutlined />, title: "Поднесите карту", meta: "Сканер или ручной ввод" },
    ok: {
      icon: <CheckCircleFilled />,
      title: field(status.card, "guestName", "") || "Зарегистрирован",
      meta: [
        status.text,
        field(cardItem || status.card, "numberName"),
        field(cardItem, "tableName"),
        field(cardItem || status.card, "effectiveMealTypeName", field(cardItem || status.card, "mealTypeName"))
      ].filter(Boolean).join(" · ")
    },
    already: {
      icon: <ExclamationCircleFilled />,
      title: field(status.card, "guestName", "") || "Уже зарегистрирован",
      meta: `Уже зарегистрирован в ${dayjs(status.registeredAt).format("HH:mm")}`
    },
    denied: { icon: <StopFilled />, title: status.text || "Отклонено", meta: field(status.card, "guestName", "") },
    notfound: { icon: <StopFilled />, title: "Карта не найдена", meta: status.text }
  }[statusInfo];

  const foundKey = card ? `${numberField(card, "idDoc")}:${numberField(card, "idStr")}` : null;

  useEffect(() => {
    if (!foundKey) return;
    document.querySelector(".nutrition-reg-card--found")?.scrollIntoView({ block: "nearest" });
  }, [foundKey, page.items]);

  const emptyText = search.trim()
    ? "Ничего не найдено"
    : filter === "waiting" ? "Все прошли" : "Никого нет";

  if (!allowed) {
    return <div className="nutrition-page"><Alert type="error" showIcon message="Нет доступа к регистрации питания (LAW83)" /></div>;
  }

  return (
    <div className="nutrition-page">
      {contextHolder}
      <div className="nutrition-toolbar">
        <h1 className="nutrition-title">Регистрация питания</h1>
      </div>
      <div className="nutrition-register-body">
        <div className="nutrition-kiosk">
          <DatePicker value={date} format="DD.MM.YYYY" allowClear={false} onChange={setDate} style={{ width: "100%" }} />
          <Segmented block value={part} options={lookupOptions(lookups.parts)} onChange={setPart} />
          <Select
            value={idBranch}
            options={[
              ...(lookups.canChooseBranch ? [{ value: 0, label: "Все филиалы" }] : []),
              ...lookupOptions(lookups.branches)
            ]}
            disabled={!lookups.canChooseBranch}
            onChange={setIdBranch}
            style={{ width: "100%" }}
          />
          <Input
            ref={scannerRef}
            value={scanner}
            size="large"
            autoComplete="off"
            placeholder="Сканируйте карту"
            onChange={(e) => setScanner(e.target.value)}
            onPressEnter={() => lookup(scanner, true)}
          />
          <div className={`nutrition-scan nutrition-scan--${statusInfo}`}>
            {statusCard.icon}
            <div className="nutrition-scan-name">{statusCard.title}</div>
            <div className="nutrition-scan-meta">{statusCard.meta}</div>
            {numberField(cardItem, "isBaby") ? <Tag color="orange">Ребёнок</Tag> : null}
            {statusInfo === "ok" && field(status.card, "registeredAt") ? (
              <div className="nutrition-scan-meta">{dayjs(field(status.card, "registeredAt")).format("HH:mm")}</div>
            ) : null}
          </div>
          <Input
            value={manual}
            placeholder="Номер карты или код считывателя"
            onChange={(e) => setManual(e.target.value)}
            onPressEnter={() => lookup(manual)}
          />
          <div style={{ display: "flex", gap: 8 }}>
            <Button icon={<SearchOutlined />} onClick={() => lookup(manual)} style={{ flex: 1 }}>Найти</Button>
            <Button
              type="primary"
              disabled={!card || !numberField(card, "eligible") || field(card, "registeredAt")}
              onClick={() => applyRegistration(card, true)}
              style={{ flex: 1 }}
            >
              Зарегистрировать
            </Button>
          </div>
        </div>
        <div className="nutrition-reg-side">
          <div className="nutrition-reg-head">
            <div className="nutrition-reg-progress">
              <Progress
                percent={page.total ? Math.round((page.registered / page.total) * 100) : 0}
                format={() => `${page.registered}/${page.total}`}
                strokeColor="#1b8c5a"
                size="small"
              />
            </div>
            <div className="nutrition-chips">
              {[["all", "Всего", page.total], ["registered", "Прошли", page.registered], ["waiting", "Ожидают", page.waiting]].map(([key, label, count]) => (
                <button key={key} type="button" className={`nutrition-chip${filter === key ? " nutrition-chip--active" : ""}`} onClick={() => setFilter(key)}>
                  {label} <b>{count}</b>
                </button>
              ))}
            </div>
            <ReportButton
              vid={REPORT_VID.NUTRITION_REGISTRATION}
              begPer={date}
              endPer={date}
              part={part}
              idBranch={idBranch || undefined}
            />
          </div>
          <Input
            allowClear
            prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
            placeholder="Поиск по имени или номеру"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="nutrition-reg-cards">
            {visibleItems.map((r) => {
              const key = `${numberField(r, "idDoc")}:${numberField(r, "idStr")}`;
              const registeredAt = field(r, "registeredAt");
              const cls = [
                "nutrition-reg-card",
                registeredAt ? "nutrition-reg-card--done" : "nutrition-reg-card--wait",
                key === busyKey ? "nutrition-reg-card--busy" : "",
                key === foundKey ? "nutrition-reg-card--found" : ""
              ].filter(Boolean).join(" ");
              return (
                <div
                  key={key}
                  className={cls}
                  onClick={() => {
                    if (!registeredAt && busyKey !== key) applyRegistration(r, true);
                  }}
                >
                  <div className="nutrition-reg-card-top">
                    <span className="nutrition-reg-card-name">
                      {field(r, "guestName", "")}
                      {numberField(r, "isBaby") ? <Tag color="orange" style={{ marginLeft: 6 }}>реб.</Tag> : null}
                    </span>
                    {registeredAt ? (
                      <span className="nutrition-reg-card-time">
                        {dayjs(registeredAt).format("HH:mm")}
                        <Popconfirm title="Отменить регистрацию?" onConfirm={() => applyRegistration(r, false)}>
                          <Button
                            danger
                            type="text"
                            size="small"
                            icon={<CloseCircleOutlined />}
                            title="Отменить регистрацию"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </Popconfirm>
                      </span>
                    ) : (
                      <span className="nutrition-reg-card-badge">Ожидает</span>
                    )}
                  </div>
                  <div className="nutrition-reg-card-meta">
                    {field(r, "numberName") && <span>{field(r, "numberName")}</span>}
                    {field(r, "tableName")
                      ? <span>{field(r, "tableName")}</span>
                      : <span className="nutrition-reg-card-dim">без стола</span>}
                    <span>{field(r, "effectiveMealTypeName", field(r, "mealTypeName", ""))}</span>
                  </div>
                </div>
              );
            })}
            {!visibleItems.length && !loading && <div className="nutrition-empty">{emptyText}</div>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NutritionRegistration;
